import useUtilStore from "@/data/util";
import { LocationLevelEnum, StoreEnum } from "@/enums";
import BoundaryApi from "@/services/Boundary";
import { BaseMap, BaseMapConfig, IBoundary, IMaybe, INotSure } from "@/types";
import { ATTRIBUTIONS, locationBoundaryDS } from "@/utils/Constant";
import { isGeoJsonObject, isStr } from "@/utils/Misc";
import { getIdxValue, storeIdxValue } from "@/utils/Storage";
import L from "leaflet";
import { StateCreator } from "zustand";
import { devtools } from "zustand/middleware";
import { shallow } from "zustand/shallow";
import { createWithEqualityFn } from "zustand/traditional";
import useRevitalizationStore from "./Revitalization";
export interface LayerState {
  center: L.LatLng;
  zoom: number;
  bounds: L.LatLngBounds | undefined;
  baseMaps: BaseMapConfig[];
  layers: IBoundary[];
  currentLocLevel: LocationLevelEnum;
  maxValue: IMaybe<number>;
}

export interface LayerActions {
  setBounds: (bounds: L.LatLngBounds | undefined) => void;
  setCenter: (center: L.LatLng) => void;
  setZoom: (zoom: number) => void;
  loadLocationBoundary: (
    id: string | string[],
    level: LocationLevelEnum,
  ) => void;
  removeLayer: (id: string | string[]) => void;
  loadBoundary: (
    payload: IBoundary[],
    level: LocationLevelEnum,
    showRD?: boolean
  ) => void;
  setCurrentLocLevel: (level: LocationLevelEnum) => void;
  setMaxValue: (maxValue: IMaybe<number>) => void;
  resetLayer: () => void;
}

const updateLayers = (
  newLayers: IBoundary[],
  level: LocationLevelEnum,
  set: INotSure
) => {
  if (newLayers[0]?.data?.features && newLayers[0]?.data?.features.length > 0) {
    updateBounds(newLayers, level, set);

    return { layers: newLayers, currentLocLevel: level };
  } else {
    return { layers: [], currentLocLevel: LocationLevelEnum.COUNTRY };
  }
};

const updateBounds = (
  layers: IBoundary[],
  level: LocationLevelEnum,
  set: INotSure
) => {
  if (layers?.length === 0) {
    set({ bounds: undefined });
    return;
  }

  const layer = layers[layers?.length - 1];

  const bounds = isGeoJsonObject(layer.data)
    ? L.geoJSON(layer.data).getBounds()
    : null;
  const center = bounds?.isValid() ? bounds.getCenter() : undefined;

  if (level === LocationLevelEnum.HF) {
    set({ bounds });
    return;
  }

  set({ bounds, center });
};

const _mapKey = import.meta.env.VITE_REACT_APP_MAP_KEY;

export const storeCreator: StateCreator<LayerState & LayerActions> = (set) => ({
  center: new L.LatLng(9.082, 8.6753),
  zoom: 7,
  bounds: undefined,
  baseMaps: [
    {
      id: BaseMap.Satellite,
      url: `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${_mapKey}`,
      attribution: ATTRIBUTIONS.join(" "),
      minZoom: 0,
      maxZoom: 22,
    },
    {
      id: BaseMap.Streets,
      url: `https://api.maptiler.com/maps/streets-v2/{z}/{x}/{y}.png?key=${_mapKey}`,
      attribution: ATTRIBUTIONS.join(" "),
      minZoom: 0,
      maxZoom: 22,
    },
    {
      id: BaseMap.Terrain,
      url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}",
      attribution:
        "Tiles & copy; Esri & mdash; Source: USGS, Esri, TANA, DeLorme, and NPS",
      minZoom: 0,
      maxZoom: 13,
    },
    {
      id: BaseMap.Topo,
      url: `https://api.maptiler.com/maps/topo-v2/{z}/{x}/{y}.png?key=${_mapKey}`,
      attribution: ATTRIBUTIONS.join(" "),
      minZoom: 0,
      maxZoom: 22,
    },
    {
      id: BaseMap.OpenStreetMap,
      url:"https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:'&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors',
      minZoom: 5,
      maxZoom: 13,
      active: true,
    },
  ],
  layers: [],
  indicators: [],
  currentLocLevel: LocationLevelEnum.COUNTRY,
  maxValue: null,
  setMaxValue: (maxValue) => set({ maxValue }),
  setBounds: (bounds) =>
    set((state) => (shallow(state.bounds, bounds) ? state : { bounds })),
  setCenter: (center) => set({ center }),
  setZoom: (zoom) => set({ zoom }),
  setCurrentLocLevel: (currentLocLevel) => set({ currentLocLevel }),
  loadLocationBoundary: async (id, level) => {
    const { setRvQuery } = useRevitalizationStore.getState();

    const ids = Array.isArray(id) ? id : [id];

    const isCountryLevel = level === LocationLevelEnum.COUNTRY;
    const canUseIndexedDB = window.indexedDB && isCountryLevel;

    try {
      if (isCountryLevel) return;
      let boundaries: IMaybe<IBoundary[]> = canUseIndexedDB
        ? (await getIdxValue(locationBoundaryDS)) || null
        : null;

      if (!boundaries) {
        boundaries = await Promise.all(
          ids.map((id) => BoundaryApi.getBoundary(id, level))
        );
        if (canUseIndexedDB)
          await storeIdxValue(locationBoundaryDS, boundaries);
      }
      set((state) => {
        const updatedLayers =
          level === LocationLevelEnum.HF
            ? [...state.layers, ...boundaries]
            : boundaries;

        return shallow(state.layers, updatedLayers)
          ? state
          : updateLayers(updatedLayers, level, set);
      });
    } catch (error) {
      console.error("Error loading location boundary:", error);
    } finally {
      setRvQuery(`?${level}_id=${id}`);
    }
  },
  loadBoundary: async (payload: IBoundary[], level, showRD = true) => {
    const { setRightDescription } = useUtilStore.getState();

    setRightDescription(showRD);

    set((state) => {
      const newLayers =
        level === LocationLevelEnum.HF
          ? [...state.layers, ...payload]
          : payload;

      return shallow(state.layers, newLayers)
        ? state
        : updateLayers(newLayers, level, set);
    });
  },
  removeLayer: (id) => {
    const idsToRemove = new Set(isStr(id) ? [id] : id);
    set((state) => {
      const newLayers = state.layers.filter(
        (layer) => !idsToRemove.has(layer.id as string)
      );
      updateBounds(newLayers, state.currentLocLevel, set);
      return { layers: newLayers };
    });
  },
  resetLayer: async () => {
    set({ layers: [], currentLocLevel: LocationLevelEnum.COUNTRY });
  },
});

const useLayerStore = createWithEqualityFn<LayerState & LayerActions>(
  devtools(storeCreator, { name: StoreEnum.LAYER }) as StateCreator<
    LayerState & LayerActions
  >,
  shallow
);

export default useLayerStore;
